/**
 * TradingFloorProps — the arena's furniture.
 *
 * ── Why props at all ─────────────────────────────────────────────────────
 *
 * The arenas were five grey boxes and a colour grade. Recolouring a box does
 * not make a place: Brawl Stars maps read instantly because they are built from
 * MANY SMALL CHUNKY OBJECTS on a tight grid — crates, bushes, fences — each
 * with one silhouette you learn in a second. The map is legible because the
 * pieces are legible.
 *
 * These are that vocabulary for a trading floor: desks, cubicle partitions,
 * monitor banks, server racks, bales of shredded tape, and the office debris
 * that sells the room.
 *
 * ── The rules that keep them readable ────────────────────────────────────
 *
 * CHUNKY. Nothing thinner than ~0.12 units. A realistic monitor bezel is one
 * pixel at the match camera and reads as noise; a fat one reads as a monitor.
 *
 * FEW PARTS. Detail below what the camera resolves costs frames and buys
 * nothing. The silhouette does the work.
 *
 * ONE GLOWING FACE. Screens are the brand, and an emissive face is the only
 * thing on a prop that survives being in shadow — which, under a single key
 * light at 55 degrees, half of every prop is.
 *
 * COLLISION IS THE PLATFORM, NOT THE MODEL. A prop is drawn INSIDE the box the
 * sim already collides against, so what you see and what you bump into cannot
 * drift apart. `PROP_SIZES` is the contract: it is what a map must size the
 * platform to, and it is exported so an arena cannot guess.
 */

import * as THREE from 'three/webgpu';
import { GRADIENT_3 } from '../ToonMaterials.js';
import { tradingScreenTexture } from '../ProceduralTextures.js';
import { PROP_SIZES } from '@chaos/sim';

/**
 * Sizes come from the sim, and the models are built to fit them.
 *
 * This table used to live here, which was fine while only a client-side mockup
 * placed props. The moment the SERVER authors an arena containing a desk, the
 * server needs to know how big a desk is — and a size table the server cannot
 * see is a client and a server disagreeing about where the walls are.
 *
 * So `PROP_SIZES` moved to `packages/sim/src/props.ts` and this file imports
 * it. The direction matters: the box is map geometry and belongs to the sim,
 * the model is art and belongs here, and the art is what has to fit the box.
 *
 * Re-exported because callers in this app import it from the art, and pointing
 * them all at the sim would be churn for no gain.
 */
export { PROP_SIZES };

// A short, flat palette. Brawl Stars props are three or four colours each —
// more reads as clutter at the distance these are actually seen from.
const C = {
  carcass: 0x2a3040,
  panel: 0x4a556b,
  panelWarm: 0x6d5748,
  metal: 0x8794a6,
  top: 0xd9e2ec,
  gold: 0xf2c14e,
  leaf: 0x3fbf6f,
  soil: 0x7a4a32,
  paper: 0xe8e2d2,
  water: 0x6fd3e8,
};

/** Cached: a session builds these once and every arena shares them. */
const _mats = new Map();

function mat(color, { emissive = null, intensity = 0.18, map = null, glow = false } = {}) {
  const key = `${color}_${emissive ?? ''}_${intensity}_${map ? map.uuid : ''}_${glow}`;
  const hit = _mats.get(key);
  if (hit) return hit;
  const m = new THREE.MeshToonNodeMaterial({
    color,
    gradientMap: GRADIENT_3,
    emissive: emissive ?? color,
    emissiveIntensity: emissive ? intensity : 0.06,
  });
  if (map) m.map = map;
  // `glow` routes the SAME texture through emissive, so the bright parts of a
  // screen are the candles and the dark parts stay dark. A flat white emissive
  // over a texture map does not light the chart, it erases it — every monitor
  // in the first build rendered as a blank white rectangle.
  if (glow && map) m.emissiveMap = map;
  _mats.set(key, m);
  return m;
}

/** One box, positioned by its CENTRE, in prop-local space (y=0 at the base). */
function box(w, h, d, x, y, z, material) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  mesh.position.set(x, y, z);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * A monitor: fat bezel, glowing chart, slight backward tilt.
 *
 * The tilt is the whole trick. A vertical screen at a 55-degree camera shows
 * almost no face; leaning it back hands the chart to the player.
 */
function monitor(x, y, z, { width = 1.1, height = 0.8, trend = 'up', rotY = 0 } = {}) {
  const g = new THREE.Group();
  g.add(box(width, height, 0.12, 0, height / 2, 0, mat(C.carcass)));
  g.add(box(width - 0.16, height - 0.16, 0.06, 0, height / 2, 0.07,
    mat(0xffffff, {
      emissive: 0xffffff,
      intensity: 1.4,
      map: tradingScreenTexture(trend),
      glow: true,
    })));
  // Stand: one post and a foot, because two posts read as a blob.
  g.add(box(0.14, 0.22, 0.14, 0, -0.11, 0, mat(C.metal)));
  g.position.set(x, y + 0.22, z);
  g.rotation.set(-0.21, rotY, 0);
  return g;
}

const BUILDERS = {
  /** Desk: top slab, two solid end panels, a keyboard, two tilted monitors. */
  desk() {
    const g = new THREE.Group();
    const [w, , d] = PROP_SIZES.desk;
    g.add(box(w, 0.16, d, 0, 0.74, 0, mat(C.top)));
    g.add(box(0.22, 0.74, d - 0.2, -w / 2 + 0.16, 0.37, 0, mat(C.panel)));
    g.add(box(0.22, 0.74, d - 0.2, w / 2 - 0.16, 0.37, 0, mat(C.panel)));
    g.add(box(1.0, 0.07, 0.36, 0, 0.855, d / 2 - 0.45, mat(C.carcass)));
    g.add(monitor(-0.75, 0.82, -0.25, { trend: 'up', rotY: 0.16 }));
    g.add(monitor(0.75, 0.82, -0.25, { trend: 'down', rotY: -0.16 }));
    return g;
  },

  /** Cubicle partition: the fence. Waist-high cover with a bright cap rail. */
  cubicle() {
    const g = new THREE.Group();
    const [w, h, d] = PROP_SIZES.cubicle;
    g.add(box(w, h - 0.18, d - 0.2, 0, (h - 0.18) / 2, 0, mat(C.panel)));
    // The cap rail is what makes a flat panel read as a solid object rather
    // than a painted wall — it gives the top edge a highlight of its own.
    g.add(box(w + 0.12, 0.18, d, 0, h - 0.09, 0, mat(C.metal)));
    g.add(box(0.16, 0.12, d + 0.14, -w / 2 + 0.2, 0.06, 0, mat(C.carcass)));
    g.add(box(0.16, 0.12, d + 0.14, w / 2 - 0.2, 0.06, 0, mat(C.carcass)));
    return g;
  },

  /** Server rack: the tall cover. Reads as a hard sight blocker from far off. */
  rack() {
    const g = new THREE.Group();
    const [w, h, d] = PROP_SIZES.rack;
    g.add(box(w, h, d, 0, h / 2, 0, mat(C.carcass)));
    // Six LED shelves. Alternating colours so it does not read as a ladder.
    for (let i = 0; i < 6; i++) {
      const y = 0.4 + i * ((h - 0.7) / 5);
      const on = i % 2 ? 0x2ee08a : 0x4aa8ff;
      g.add(box(w - 0.22, 0.16, 0.08, 0, y, d / 2 + 0.01,
        mat(on, { emissive: on, intensity: 1.1 })));
    }
    g.add(box(w + 0.14, 0.14, d + 0.14, 0, h - 0.07, 0, mat(C.metal)));
    return g;
  },

  /** Monitor bank: six screens on a frame. The arena's landmark. */
  screens() {
    const g = new THREE.Group();
    const [w, h, d] = PROP_SIZES.screens;
    g.add(box(w, 0.22, d, 0, 0.11, 0, mat(C.carcass)));
    g.add(box(0.24, h - 0.2, 0.3, -w / 2 + 0.16, h / 2, 0, mat(C.metal)));
    g.add(box(0.24, h - 0.2, 0.3, w / 2 - 0.16, h / 2, 0, mat(C.metal)));
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < 3; col++) {
        g.add(monitor((col - 1) * 1.25, 0.95 + row * 1.05, 0.12, {
          width: 1.15,
          height: 0.86,
          // Mixed trends: a wall where every chart agrees looks like one image
          // stretched across six panels.
          trend: (row + col) % 2 ? 'down' : 'up',
        }));
      }
    }
    return g;
  },

  /** Bale of shredded tape: the bush. Soft cover, obviously not a wall. */
  bale() {
    const g = new THREE.Group();
    const [w, h] = PROP_SIZES.bale;
    // Three offset slabs instead of one box: the broken silhouette is what
    // says "you can push through this" versus the rack's hard rectangle.
    g.add(box(w, h * 0.55, w, 0, h * 0.28, 0, mat(C.paper)));
    g.add(box(w * 0.8, h * 0.5, w * 0.8, -0.16, h * 0.66, 0.12, mat(0xd8d0bd)));
    g.add(box(w * 0.62, h * 0.42, w * 0.62, 0.22, h * 0.72, -0.14, mat(0xf2eee0)));
    g.add(box(w * 0.9, 0.1, w * 0.9, 0, 0.05, 0, mat(C.carcass)));
    return g;
  },

  /** Potted plant: every office has one, and it breaks up a room of boxes. */
  plant() {
    const g = new THREE.Group();
    g.add(box(0.7, 0.55, 0.7, 0, 0.27, 0, mat(C.soil)));
    g.add(box(0.8, 0.14, 0.8, 0, 0.58, 0, mat(C.panelWarm)));
    g.add(box(0.14, 0.7, 0.14, 0, 0.95, 0, mat(0x2f7d4a)));
    const leaf = (x, z, ry) => {
      const m = box(0.95, 0.12, 0.42, x, 1.42, z, mat(C.leaf));
      m.rotation.set(0.22, ry, 0.3);
      return m;
    };
    g.add(leaf(0.3, 0.08, 0.4), leaf(-0.3, -0.1, 2.4), leaf(0.05, -0.3, 1.4));
    return g;
  },

  /** Water cooler. */
  cooler() {
    const g = new THREE.Group();
    g.add(box(0.8, 0.9, 0.8, 0, 0.45, 0, mat(C.top)));
    g.add(box(0.34, 0.1, 0.2, 0, 0.72, 0.42, mat(C.carcass)));
    g.add(box(0.62, 0.75, 0.62, 0, 1.32, 0, mat(C.water, { emissive: C.water, intensity: 0.35 })));
    g.add(box(0.3, 0.14, 0.3, 0, 1.76, 0, mat(C.metal)));
    return g;
  },

  /** Stack of settled coins — the only pure loot-read object in the set. */
  coins() {
    const g = new THREE.Group();
    const disc = (x, y, z, r) => {
      const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, 0.14, 12),
        mat(C.gold, { emissive: C.gold, intensity: 0.4 }));
      m.position.set(x, y, z);
      m.castShadow = true;
      return m;
    };
    for (let i = 0; i < 4; i++) g.add(disc(0, 0.08 + i * 0.15, 0, 0.38));
    g.add(disc(0.42, 0.08, 0.2, 0.3));
    g.add(disc(-0.36, 0.08, -0.24, 0.3));
    g.add(disc(-0.36, 0.23, -0.24, 0.3));
    return g;
  },

  /** Office chair. Decoration, but it is what makes a desk read as occupied. */
  chair() {
    const g = new THREE.Group();
    g.add(box(0.62, 0.14, 0.6, 0, 0.5, 0, mat(C.panel)));
    g.add(box(0.6, 0.6, 0.14, 0, 0.85, -0.24, mat(C.panel)));
    g.add(box(0.16, 0.4, 0.16, 0, 0.24, 0, mat(C.metal)));
    g.add(box(0.7, 0.1, 0.16, 0, 0.06, 0, mat(C.carcass)));
    g.add(box(0.16, 0.1, 0.7, 0, 0.06, 0, mat(C.carcass)));
    return g;
  },
};

export function isProp(kind) {
  return Object.hasOwn(BUILDERS, kind);
}

/**
 * Builds one prop, with its base at y=0 and centred on x/z.
 *
 * Returns null for an unknown kind rather than throwing: a map that names a
 * prop this build does not have should render as the plain platform it always
 * was, not take the whole arena down with it.
 */
export function buildProp(kind) {
  const build = BUILDERS[kind];
  if (!build) return null;
  const group = build();
  group.name = `prop:${kind}`;
  return group;
}

/** Frees every shared prop material. Call when the session tears the scene down. */
export function disposeProps() {
  for (const m of _mats.values()) m.dispose();
  _mats.clear();
}
